import flatMapDeep from 'lodash/flatMapDeep';
import isEmpty from 'lodash/isEmpty';
import { travelerForm } from '@eti/travelers';
import {
  MEAL,
  SIMPLE_VISA,
  ANCILLARY_BUNDLE,
  TRIP_CANCELLATION_PROTECTION,
  ALL_INCLUSIVE_PROTECTION,
} from '@eti/products';
import { generateBundleValues } from '../../../utils/addedAncillaryBundles';
import { getFrequentFlyerData } from './frequentFlyer';

const { FREQUENT_FLYER, TRAVEL_DOCUMENT_ISSUING_COUNTRY } = travelerForm;

const transformMealChoices = (travelerChoices) =>
  Object.values(travelerChoices)
    .filter((travelerChoice) => travelerChoice.value !== 'NO_OPTION')
    .map((travelerChoice) => ({
      choiceId: Number(travelerChoice.choiceId),
      travelerId: Number(travelerChoice.travelerId),
    }));

const transformManulifeChoices = (travelerChoices) => {
  const isSelected = travelerChoices?.value?.value;
  const province = travelerChoices?.province?.value;

  if (!isSelected || !province) {
    return [];
  }

  const travelers = travelerChoices?.travelers ?? {};
  return Object.values(travelers).reduce(
    (acc, { id, selected } = {}) =>
      selected ? [...acc, { choiceId: Number(province), travelerId: Number(id) }] : acc,
    [],
  );
};

const transformSimpleVisaChoices = (travelerChoices) => {
  const isSelected = travelerChoices?.value?.value;

  if (!isSelected) {
    return [];
  }

  const travelers = travelerChoices?.travelers ?? {};

  return Object.values(travelers).reduce(
    (acc, { choice: { id, value }, selected } = {}) =>
      selected && Number(value)
        ? [...acc, { choiceId: Number(value), travelerId: Number(id) }]
        : acc,
    [],
  );
};

const transformChoices = (id, travelerChoices) => {
  switch (Number(id)) {
    case MEAL.id:
      return transformMealChoices(travelerChoices);
    case SIMPLE_VISA.id:
      return transformSimpleVisaChoices(travelerChoices);
    case ALL_INCLUSIVE_PROTECTION.id:
    case TRIP_CANCELLATION_PROTECTION.id:
    default:
      return transformManulifeChoices(travelerChoices);
  }
};

export const transformTravelerChoices = (id, travelerChoices) => ({
  productId: id,
  selectionTravelerChoice: transformChoices(id, travelerChoices),
});

const checkIfSegmentHasSeats = (segment) =>
  Boolean(
    segment &&
      (segment.column || (segment.seatPreference && segment.seatPreference !== 'NO_OPTION')),
  );

const transformSeatMapObject = (
  boundIndex,
  segmentIndex,
  { id: travelerId, row, column, seatPreference },
) => ({
  travelerId: typeof travelerId === 'number' ? String(travelerId) : travelerId,
  row,
  column,
  boundIndex: Number(boundIndex),
  segmentIndex,
  ...(seatPreference ? { seatPreference } : {}),
});

const getSeatMap = (segments) =>
  flatMapDeep(segments, (segment, boundSegmentIndex) => {
    const boundSegment = boundSegmentIndex.split('-', 2);

    const boundIndex = Number(boundSegment[0]);
    const segmentIndex = Number(boundSegment[1]);
    return segment
      ? Object.keys(segment)
          .filter((key) => checkIfSegmentHasSeats(segment[key]))
          .map((key) =>
            transformSeatMapObject(boundIndex, segmentIndex, {
              ...segment[key],
            }),
          )
      : null;
  });

export const transformSeatmap = (id, value) => {
  const { selected, ...segments } = value;
  return {
    productId: id,
    selectionSeatMap: selected ? getSeatMap(segments) : [],
  };
};

export const getProductSpecs = (products, id) =>
  products.find(
    (product) =>
      String(product.id) === id ||
      (Array.isArray(product.ids) &&
        product.ids.includes(Number(id)) &&
        product.id !== ANCILLARY_BUNDLE.id),
  ) || {};

export const transformAncillaryBundles = (
  formValues = {},
  isAncillaryBundlingEnabled,
  bundleData,
) => {
  const ancillaryBundleSelection = formValues.bundles && [
    ...formValues.bundles?.map((bundle) => ({
      bundleId: bundle.id,
      optionId: bundle.value,
    })),
  ];
  if (isAncillaryBundlingEnabled) {
    const ancillaryBundleSelectionTDPage = generateBundleValues(
      bundleData,
      formValues.ancillaryBundle,
    )?.map((bundle) => ({
      bundleId: bundle.id,
      optionId: bundle.value,
    }));

    if (ancillaryBundleSelectionTDPage.length > 0) {
      if (!ancillaryBundleSelection) {
        return ancillaryBundleSelectionTDPage;
      }
      ancillaryBundleSelection.push(...ancillaryBundleSelectionTDPage);
    }
  }

  return ancillaryBundleSelection;
};

export const transformInputToDate = (input = '', format) => {
  const pattern = format.split('-');
  const day = pattern.indexOf('dd');
  const month = pattern.indexOf('MM');
  const year = pattern.indexOf('yyyy');
  const inputArray = input.split('-');
  return new Date(Date.UTC(inputArray[year], Number(inputArray[month]) - 1, inputArray[day]));
};

const getTravelerDateOfBirthFromManulife = (formValues, travelerId) => {
  const allInclusiveProtection = formValues?.allInclusiveProtection?.travelers;
  const tripCancellationProtection = formValues?.tripCancellationProtection?.travelers;

  if (!(allInclusiveProtection || tripCancellationProtection)) {
    return '';
  }

  const getTravelerDateOfBirthIn = (...products) =>
    products
      .map((product = {}) => {
        const traveler = Object.values(product).find(
          ({ id } = {}) => Number(travelerId) === Number(id),
        );
        return traveler?.dateOfBirth ?? '';
      })
      .filter(Boolean);

  const [dateOfBirth] = getTravelerDateOfBirthIn(
    allInclusiveProtection,
    tripCancellationProtection,
  );

  return dateOfBirth;
};

export const transformTravelers = (formValues = {}, travelers = [], dateFormat) => {
  const travelerData = Object.entries(formValues)
    .filter(([key]) => key.startsWith('traveler-section'))
    .filter(([, value]) => !isEmpty(value));

  return travelerData.map(([key, value]) => {
    const [, , id] = key.split('-');
    const { ageType } = travelers.find((tripTraveler) => Number(tripTraveler.id) === Number(id));
    const travelDocumentIssuingCountry = value[TRAVEL_DOCUMENT_ISSUING_COUNTRY];
    const gender = value[`traveler-gender-${id}`];
    const frequentFlyerCard = getFrequentFlyerData(value[FREQUENT_FLYER]);
    const birthDateValue =
      value[`traveler-date-of-birth-form-${id}`] ||
      getTravelerDateOfBirthFromManulife(formValues, id);
    const birthDate = birthDateValue
      ? transformInputToDate(birthDateValue, dateFormat).toISOString()
      : null;
    return {
      ageType,
      birthDate,
      gender: gender && gender.toUpperCase(),
      id,
      firstName: value[`traveler-firstName-${id}`],
      lastName: value[`traveler-lastName-${id}`],
      travelDocumentInformation: {
        cpfNumber: value[`traveler-travel-cpf-number-${id}`],
        birthDate,
        expiryDate: value[`traveler-travel-document-expiry-date-${id}`]
          ? transformInputToDate(
              value[`traveler-travel-document-expiry-date-${id}`],
              dateFormat,
            ).toISOString()
          : null,
        travelDocumentNumber: value[`traveler-travel-document-number-${id}`],
        nationalityId: travelDocumentIssuingCountry && Number(travelDocumentIssuingCountry.value),
      },
      frequentFlyerCard,
    };
  });
};
